import React, { useEffect, useState } from "react";
import {
    Container, Row, Col, Card, CardBody, Button, Spinner,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Input, FormGroup, Label, Badge, Table
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { getResidentData } from "../../helpers/residentauth";
import { toast } from "react-toastify";

const api = new APIClient();

interface Tenant {
    id: number;
    name: string;
    email?: string;
    phone: string;
    isActive: boolean;
    units: { id: number; unitNo: string; floor: number }[];
}

interface Unit {
    id: number;
    unitNo: string;
    floor: number;
}

const ResidentTenants: React.FC = () => {
    const residentData = getResidentData();
    const allUnits = residentData?.unit || [];

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        unitId: ""
    });

    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchTenants = async () => {
        try {
            setLoading(true);
            const res: any = await api.get("/api/tenants/my-tenants");
            setTenants(res?.data || []);
        } catch (err) {
            toast.error("Failed to load tenants");
        } finally {
            setLoading(false);
        }
    };

    const availableUnits = allUnits.filter((u: Unit) => {
        const hasTenant = tenants.some(t =>
            t.units.some(tu => tu.id === u.id)
        );
        return !hasTenant;
    });

    const handleAdd = () => {
        if (availableUnits.length === 0) {
            toast.warning("All units have tenants. Remove one first.");
            return;
        }
        setEditMode(false);
        setSelectedTenant(null);
        setFormData({
            name: "",
            email: "",
            phone: "",
            unitId: availableUnits[0].id.toString()
        });
        setShowModal(true);
    };

    const handleEdit = (tenant: Tenant) => {
        setEditMode(true);
        setSelectedTenant(tenant);
        setFormData({
            name: tenant.name,
            email: tenant.email || "",
            phone: tenant.phone,
            unitId: tenant.units[0]?.id.toString() || ""
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.warning("Name required");
            return;
        }
        if (formData.phone.length !== 10) {
            toast.warning("Valid phone required");
            return;
        }

        setSaving(true);
        try {
            if (editMode && selectedTenant) {
                await api.put(`/api/tenants/${selectedTenant.id}`, {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone
                });
                toast.success("Tenant updated!");
            } else {
                if (!formData.unitId) {
                    toast.warning("Select unit");
                    setSaving(false);
                    return;
                }
                await api.create("/api/tenants/add", {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    unitId: parseInt(formData.unitId)
                });
                toast.success("Tenant added! 🎉");
            }

            setShowModal(false);
            fetchTenants();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (tenant: Tenant) => {
        if (!window.confirm(`Remove tenant "${tenant.name}"?`)) return;

        try {
            await api.delete(`/api/tenants/${tenant.id}`);
            toast.success("Tenant removed!");
            fetchTenants();
        } catch (err: any) {
            toast.error("Failed to remove");
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading tenants...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: "0 4px" }}>
            <style>{`
                /* ── Responsive overrides ── */

                /* Header */
                .rt-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 20px;
                }
                .rt-header-btn {
                    flex-shrink: 0;
                    white-space: nowrap;
                }

                /* Stat cards */
                .rt-stats-row {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                }
                @media (max-width: 480px) {
                    .rt-stats-row {
                        grid-template-columns: 1fr;
                    }
                    .rt-stat-box {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        text-align: left !important;
                        padding: 12px 16px !important;
                    }
                    .rt-stat-number {
                        font-size: 22px !important;
                        min-width: 36px;
                    }
                }

                /* Desktop table – hidden on mobile */
                .rt-table-wrap {
                    display: block;
                }
                @media (max-width: 767px) {
                    .rt-table-wrap {
                        display: none;
                    }
                }

                /* Mobile card list – hidden on desktop */
                .rt-card-list {
                    display: none;
                }
                @media (max-width: 767px) {
                    .rt-card-list {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                }

                /* Tenant mobile card */
                .rt-tenant-card {
                    border: 1px solid var(--border-color);
                    border-radius: 10px;
                    padding: 14px 16px;
                    background: var(--bg-item);
                }
                .rt-tenant-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 8px;
                }
                .rt-tenant-actions {
                    display: flex;
                    gap: 6px;
                    flex-shrink: 0;
                    margin-left: 8px;
                }
                .rt-tenant-detail {
                    font-size: 13px;
                    color: var(--text-secondary);
                    margin-bottom: 4px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .rt-tenant-detail a {
                    color: var(--text-primary);
                }

                /* Modal full-screen on mobile */
                @media (max-width: 575px) {
                    .modal-dialog {
                        margin: 0 !important;
                        max-width: 100% !important;
                        width: 100% !important;
                    }
                    .modal-content {
                        border-radius: 16px 16px 0 0 !important;
                        min-height: 60vh;
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                    }
                }

                /* Action buttons: comfortable touch size */
                .rt-btn-icon {
                    width: 36px;
                    height: 36px;
                    padding: 0 !important;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px !important;
                }
            `}</style>

            {/* ── Header ── */}
            <div className="rt-header">
                <div>
                    <h4 className="fw-bold mb-1" style={{ fontSize: "clamp(16px, 4vw, 20px)" }}>
                        <i className="ri-team-line me-2 text-primary" />
                        My Tenants
                    </h4>
                    <small className="text-muted">
                        Manage tenants for your units ({tenants.length} active)
                    </small>
                </div>
                <Button color="primary" onClick={handleAdd} className="rt-header-btn">
                    <i className="ri-add-line me-1" />
                    Add Tenant
                </Button>
            </div>

            {/* ── Stats ── */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: "12px", background: "var(--bg-card)" }}>
                <CardBody style={{ padding: "16px" }}>
                    <div className="rt-stats-row">
                        <div
                            className="rt-stat-box text-center p-3"
                            style={{ background: "rgba(59, 130, 246, 0.1)", borderRadius: "10px" }}
                        >
                            <div
                                className="rt-stat-number fw-bold"
                                style={{ fontSize: 28, color: "#3B82F6" }}
                            >
                                {allUnits.length}
                            </div>
                            <small className="text-muted">Total Units</small>
                        </div>
                        <div
                            className="rt-stat-box text-center p-3"
                            style={{ background: "rgba(34, 197, 94, 0.1)", borderRadius: "10px" }}
                        >
                            <div
                                className="rt-stat-number fw-bold"
                                style={{ fontSize: 28, color: "#22C55E" }}
                            >
                                {tenants.length}
                            </div>
                            <small className="text-muted">With Tenants</small>
                        </div>
                        <div
                            className="rt-stat-box text-center p-3"
                            style={{ background: "rgba(245, 158, 11, 0.1)", borderRadius: "10px" }}
                        >
                            <div
                                className="rt-stat-number fw-bold"
                                style={{ fontSize: 28, color: "#F59E0B" }}
                            >
                                {availableUnits.length}
                            </div>
                            <small className="text-muted">Self Occupied</small>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* ── Tenant List ── */}
            <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", background: "var(--bg-card)" }}>
                <CardBody style={{ padding: "16px" }}>
                    {tenants.length > 0 ? (
                        <>
                            {/* Desktop Table */}
                            <div className="rt-table-wrap">
                                <Table hover className="align-middle mb-0" style={{ color: "var(--text-primary)" }}>
                                    <thead style={{ borderBottom: "1px solid var(--border-color)" }}>
                                        <tr>
                                            <th>#</th>
                                            <th>Tenant Name</th>
                                            <th>Phone</th>
                                            <th>Email</th>
                                            <th>Unit</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tenants.map((t, i) => (
                                            <tr key={t.id} style={{ borderColor: "var(--border-color)" }}>
                                                <td>{i + 1}</td>
                                                <td className="fw-semibold">{t.name}</td>
                                                <td>
                                                    <a href={`tel:${t.phone}`} style={{ color: "var(--text-primary)" }}>
                                                        <i className="ri-phone-line me-1" />
                                                        {t.phone}
                                                    </a>
                                                </td>
                                                <td>{t.email || <span className="text-muted">—</span>}</td>
                                                <td>
                                                    {t.units.map(u => (
                                                        <Badge key={u.id} color="primary" pill className="me-1">
                                                            {u.unitNo}
                                                        </Badge>
                                                    ))}
                                                </td>
                                                <td>
                                                    <Button
                                                        className="rt-btn-icon me-1"
                                                        color="info"
                                                        outline
                                                        onClick={() => handleEdit(t)}
                                                        title="Edit tenant"
                                                    >
                                                        <i className="ri-edit-line" />
                                                    </Button>
                                                    <Button
                                                        className="rt-btn-icon"
                                                        color="danger"
                                                        outline
                                                        onClick={() => handleRemove(t)}
                                                        title="Remove tenant"
                                                    >
                                                        <i className="ri-delete-bin-line" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="rt-card-list">
                                {tenants.map((t, i) => (
                                    <div key={t.id} className="rt-tenant-card">
                                        <div className="rt-tenant-card-header">
                                            <div>
                                                <div className="fw-semibold" style={{ fontSize: 15 }}>
                                                    {t.name}
                                                </div>
                                                <div style={{ marginTop: 4 }}>
                                                    {t.units.map(u => (
                                                        <Badge key={u.id} color="primary" pill className="me-1">
                                                            {u.unitNo}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="rt-tenant-actions">
                                                <Button
                                                    className="rt-btn-icon"
                                                    color="info"
                                                    outline
                                                    onClick={() => handleEdit(t)}
                                                    title="Edit"
                                                >
                                                    <i className="ri-edit-line" />
                                                </Button>
                                                <Button
                                                    className="rt-btn-icon"
                                                    color="danger"
                                                    outline
                                                    onClick={() => handleRemove(t)}
                                                    title="Remove"
                                                >
                                                    <i className="ri-delete-bin-line" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="rt-tenant-detail">
                                            <i className="ri-phone-line" />
                                            <a href={`tel:${t.phone}`}>{t.phone}</a>
                                        </div>
                                        {t.email && (
                                            <div className="rt-tenant-detail">
                                                <i className="ri-mail-line" />
                                                <span>{t.email}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-5">
                            <div style={{ fontSize: 56 }}>👥</div>
                            <h5 className="text-muted mt-2">No tenants yet</h5>
                            <p className="text-muted" style={{ fontSize: 14 }}>
                                Tap "Add Tenant" to get started
                            </p>
                            <Button color="primary" outline onClick={handleAdd} className="mt-1">
                                <i className="ri-add-line me-1" />
                                Add Tenant
                            </Button>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* ── Add / Edit Modal ── */}
            <Modal
                isOpen={showModal}
                toggle={() => !saving && setShowModal(false)}
                centered
                style={{ maxWidth: 500 }}
                contentClassName="bg-card"
            >
                <ModalHeader toggle={() => !saving && setShowModal(false)} className="border-0">
                    <span style={{ color: "var(--text-primary)" }}>
                        {editMode ? "Edit Tenant" : "Add New Tenant"}
                    </span>
                </ModalHeader>
                <ModalBody style={{ padding: "20px 24px" }}>
                    <FormGroup>
                        <Label style={{ color: "var(--text-primary)" }}>Tenant Name *</Label>
                        <Input
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Full name"
                            style={{ borderRadius: 8, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label style={{ color: "var(--text-primary)" }}>Phone Number *</Label>
                        <Input
                            type="tel"
                            inputMode="numeric"
                            value={formData.phone}
                            onChange={e => {
                                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                                setFormData({ ...formData, phone: v });
                            }}
                            placeholder="10-digit mobile number"
                            maxLength={10}
                            style={{ borderRadius: 8, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                        />
                        <small className="text-muted">Tenant will use this for Quick Pay</small>
                    </FormGroup>

                    <FormGroup>
                        <Label style={{ color: "var(--text-primary)" }}>Email <span className="text-muted">(Optional)</span></Label>
                        <Input
                            type="email"
                            inputMode="email"
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            placeholder="tenant@example.com"
                            style={{ borderRadius: 8, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                        />
                        <small className="text-muted">For receiving OTP and receipts</small>
                    </FormGroup>

                    {!editMode && (
                        <FormGroup>
                            <Label style={{ color: "var(--text-primary)" }}>Assign to Unit *</Label>
                            <Input
                                type="select"
                                value={formData.unitId}
                                onChange={e => setFormData({ ...formData, unitId: e.target.value })}
                                style={{ borderRadius: 8, background: "var(--bg-item)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}
                            >
                                <option value="">Select unit</option>
                                {availableUnits.map((u: Unit) => (
                                    <option key={u.id} value={u.id}>
                                        {u.unitNo} (Floor {u.floor})
                                    </option>
                                ))}
                            </Input>
                            <small className="text-muted">
                                {availableUnits.length} unit{availableUnits.length !== 1 ? "s" : ""} available
                            </small>
                        </FormGroup>
                    )}

                    <div style={{
                        background: "rgba(59, 130, 246, 0.1)",
                        padding: "12px 14px",
                        borderRadius: 8,
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: "var(--text-primary)"
                    }}>
                        <i className="ri-information-line me-1 text-info" />
                        Tenant will receive bills for this unit and can pay via Quick Pay using their phone number.
                    </div>
                </ModalBody>
                <ModalFooter className="border-0" style={{ gap: 8, padding: "12px 24px 20px" }}>
                    <Button
                        color="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={saving}
                        style={{ flex: 1, borderRadius: 8 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSave}
                        disabled={saving}
                        style={{ flex: 2, borderRadius: 8 }}
                    >
                        {saving ? (
                            <><Spinner size="sm" className="me-1" />Saving...</>
                        ) : (
                            editMode ? "Update Tenant" : "Add Tenant"
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ResidentTenants;